import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import AppBar from '@material-ui/core/AppBar';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import PositionForm from '../../../components/PositionForm';
import Address from '@afosto/app-components-react/Address';
import Dialog from '@afosto/app-components-react/Dialog';
import Icon from '@afosto/app-components-react/Icon';
import Property from '@afosto/app-components-react/Property';
import RegisterFormProvider from '@afosto/app-components-react/RegisterFormProvider';
import fetchShipmentAddressing from '../../../utils/fetchShipmentAddressing';
import fetchShippingMethod from '../../../utils/fetchShippingMethod';
import getShippingMethodName from '../../../utils/getShippingMethodName';
import getFullName from '@afosto/utils/getFullName';
import { datetime as datetimeFormatter } from '@afosto/app-components-react/DataGrid/formatters';
import { parcels as parcelsApi } from '@afosto/fulfilment/api/Parcels';
import { shipments as shipmentsApi } from '@afosto/fulfilment/api/Shipments';
import translations from './translations';

const ParcelDialog = ({ disableBackdropClick, fullscreen, isOpen, onClose, onSave, parcel }) => {
  const intl = useIntl();
  const [locationId, setLocationId] = useState(null);
  const [shipment, setShipment] = useState(null);
  const [shippingMethod, setShippingMethod] = useState(null);
  const [addresses, setAddresses] = useState(null);
  const formRef = useRef(undefined);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchShipment = async () => {
      try {
        const response = await shipmentsApi.viewShipment(parcel?.shipment_id);
        const { data } = response;

        if (data?.is_outbound && data?.addressing?.from?.entity_type === 'location') {
          setLocationId(data?.addressing?.from?.entity_id);
        } else if (!data?.is_outbound && data?.addressing?.to?.entity_type === 'location') {
          setLocationId(data?.addressing?.to?.entity_id);
        }

        setShipment(data);
      } catch (err) {
        setShipment(null);
      }
    };

    if (parcel) {
      fetchShipment();
    }
  }, [parcel]);

  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const shipmentAddressing = await fetchShipmentAddressing(shipment, ['from']);
        setAddresses(shipmentAddressing);
      } catch (err) {
        setAddresses({});
      }
    };

    const fetchMethod = async () => {
      try {
        const method = await fetchShippingMethod(shipment?.method_id);
        setShippingMethod(method);
      } catch (err) {
        setShippingMethod(null);
      }
    };

    if (shipment?.addressing) {
      fetchAddresses();
    }

    if (shipment?.method_id) {
      fetchMethod();
    }
  }, [shipment]);

  const handleSubmit = async ({ position }) => {
    try {
      await parcelsApi.updateParcel(parcel?.id, {
        weight: parcel?.weight,
        length: parcel?.length,
        width: parcel?.width,
        height: parcel?.height,
        tracking_code: parcel?.tracking_code,
        tracking_url: parcel?.tracking_url,
        ship_at: parcel?.ship_at,
        position,
      });

      enqueueSnackbar(intl.formatMessage(translations.feedback.parcelPositionUpdated), {
        variant: 'success',
      });

      if (onSave && typeof onSave === 'function') {
        onSave();
      }
    } catch (error) {
      if (error?.response?.status === 400) {
        formRef.current.setError(
          'position',
          'unknownPosition',
          "This position doesn't exist, please use a registered position",
        );
      } else {
        enqueueSnackbar(intl.formatMessage(translations.feedback.parcelPositionNotUpdated), {
          variant: 'error',
        });
      }
    }
  };

  const handleClose = () => {
    if (onClose && typeof onClose === 'function') {
      onClose();
    }
  };

  return (
    <RegisterFormProvider value={{ formRef }}>
      <Dialog
        disableBackdropClick={disableBackdropClick}
        open={isOpen}
        fullScreen={fullscreen}
        onClose={handleClose}
        scroll="paper"
        fullWidth
      >
        <AppBar position="sticky" color="default">
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
              <Icon icon={['fal', 'times']} />
            </IconButton>
            <Typography variant="h6">{parcel?.number}</Typography>
          </Toolbar>
        </AppBar>
        <DialogContent>
          <Paper elevation={0} style={{ position: 'sticky', top: 0 }}>
            <PositionForm locationId={locationId} onSubmit={handleSubmit} />
          </Paper>
          <Grid container spacing={1}>
            <Grid item xs={12} md={6}>
              <Property
                label={intl.formatMessage(translations.properties.shippingNumber.label)}
                value={shipment?.number}
              />
              <Property
                label={intl.formatMessage(translations.properties.shippingMethod.label)}
                value={getShippingMethodName(intl, shippingMethod)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Property
                label={intl.formatMessage(translations.properties.expectedAt.label)}
                value={shipment?.expected_at ? datetimeFormatter(shipment.expected_at) : undefined}
              />
              <Property
                label={intl.formatMessage(translations.properties.shipAt.label)}
                value={parcel?.ship_at ? datetimeFormatter(parcel.ship_at) : undefined}
              />
            </Grid>
          </Grid>
          <Grid container spacing={1}>
            <Grid item xs={12} md={6}>
              <Property
                label={intl.formatMessage(translations.properties.toAddress.label)}
                value={
                  addresses?.to && (
                    <>
                      {addresses?.to?.location_data && (
                        <Typography variant="body2">
                          {addresses?.to?.location_data?.name}
                        </Typography>
                      )}
                      {addresses?.to?.postal_code && (
                        <Address
                          company={addresses.to?.organisation}
                          fullName={getFullName(addresses.to)}
                          addressLine1={addresses.to?.address_line_1}
                          addressLine2={addresses.to?.address_line_2}
                          postalCode={addresses?.to?.postal_code}
                          city={addresses?.to?.locality}
                          country={addresses?.to?.country_code}
                          administrativeArea={addresses?.to?.administrative_area}
                        />
                      )}
                    </>
                  )
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={() => formRef?.current?.onSubmit()}>
            <FormattedMessage {...translations.actions.save} />
          </Button>
        </DialogActions>
      </Dialog>
    </RegisterFormProvider>
  );
};

ParcelDialog.propTypes = {
  disableBackdropClick: PropTypes.bool,
  fullscreen: PropTypes.bool,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  parcel: PropTypes.object,
};

ParcelDialog.defaultProps = {
  disableBackdropClick: false,
  fullscreen: false,
  isOpen: false,
  onClose: undefined,
  onSave: undefined,
  parcel: undefined,
};

export default ParcelDialog;
